import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * Resolves whether the currently signed-in user is flagged as a
 * superadmin in orders.customer_users. Drives whether the Admin nav link
 * is visible and whether /admin routes render their content.
 *
 * Returns `false` (not undefined) when there is no signed-in user, so the
 * sidebar can render without a flash of admin chrome during boot.
 */
export function useIsSuperadmin(): { isSuperadmin: boolean; loading: boolean } {
  const { data, isLoading } = useQuery<boolean>({
    queryKey: ['is-superadmin'],
    queryFn: async () => {
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData.user?.id;
      if (!userId) return false;
      const { data, error } = await supabase
        .schema('orders')
        .from('customer_users')
        .select('is_superadmin')
        .eq('id', userId)
        .maybeSingle();
      if (error) return false;
      return data?.is_superadmin === true;
    },
    staleTime: 5 * 60 * 1000,
  });
  return { isSuperadmin: data === true, loading: isLoading };
}
